import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { useNavigate, Link } from 'react-router-dom';

interface SupportIssue {
    id: string;
    email: string;
    phone: string;
    issue: string;
    createdAt: any;
    status: string;
}

const SupportAdmin: React.FC = () => {
    const [issues, setIssues] = useState<SupportIssue[]>([]);
    const [loading, setLoading] = useState(true);
    const [userRole, setUserRole] = useState<string | null>(null);
    const [editIssue, setEditIssue] = useState<SupportIssue | null>(null);
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [modalError, setModalError] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                // Fetch user role from Firestore
                const userDoc = await import('firebase/firestore').then(firestore => firestore.getDoc(firestore.doc(db, 'users', user.uid)));
                if (userDoc.exists()) {
                    const data = userDoc.data();
                    setUserRole(data.role || null);
                } else {
                    setUserRole(null);
                }
            } else {
                setUserRole(null);
            }
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (userRole !== 'sa') {
            setLoading(false);
            return;
        }
        const fetchIssues = async () => {
            setLoading(true);
            const querySnapshot = await getDocs(collection(db, 'support'));
            const issuesArr: SupportIssue[] = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as SupportIssue[];
            setIssues(issuesArr);
            setLoading(false);
        };
        fetchIssues();
    }, [userRole]);

    const handleEditClick = (issue: SupportIssue) => {
        setEditIssue({ ...issue });
        setModalError('');
    };

    const handleModalChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        if (!editIssue) return;
        setEditIssue({ ...editIssue, [e.target.name]: e.target.value });
    };

    const handleModalSave = async () => {
        if (!editIssue) return;
        setStatusUpdating(true);
        setModalError('');
        try {
            await updateDoc(doc(db, 'support', editIssue.id), {
                email: editIssue.email,
                phone: editIssue.phone,
                issue: editIssue.issue,
                status: editIssue.status,
            });
            setIssues(prev => prev.map(issue => issue.id === editIssue.id ? { ...editIssue } : issue));
            setEditIssue(null);
        } catch (err: any) {
            setModalError(err.message || 'Failed to update issue.');
        }
        setStatusUpdating(false);
    };

    const handleModalClose = () => {
        setEditIssue(null);
        setModalError('');
    };

    const handleLogout = async () => {
        const auth = getAuth();
        await signOut(auth);
        navigate('/signin');
    };

    if (loading) return <div className="text-center py-10">Loading...</div>;
    if (userRole !== 'sa') return <div className="text-center py-10 text-red-500">Access denied.</div>;

    return (
        <div className="min-h-screen bg-gray-900 flex flex-col">
            {/* Navigation/Header */}
            <nav className="bg-gray-800 py-4">
                <div className="container mx-auto px-4 sm:px-6 flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0">
                    <div className="flex items-center gap-3">
                        <img src="/images/logo.png" alt="Fixisy Logo" className="h-8 w-8 sm:h-10 sm:w-10 object-contain" />
                        <Link to="/" className="text-xl sm:text-2xl font-bold text-cyan-400">Fixisy</Link>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-center">
                        <Link to="/dashboard" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-semibold">Back to Dashboard</Link>
                        <span className="text-gray-300 text-xs sm:text-sm">Contact: <a href="mailto:support@fixisy.com" className="text-cyan-400 hover:underline">support@fixisy.com</a></span>
                        <Link to="/support" className="ml-0 sm:ml-4 px-4 py-2 bg-cyan-700 text-white rounded hover:bg-cyan-800 font-semibold transition-colors w-full sm:w-auto text-center">Support</Link>
                        <button onClick={handleLogout} className="ml-0 sm:ml-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 font-semibold transition-colors w-full sm:w-auto text-center">Logout</button>
                    </div>
                </div>
            </nav>
            {/* Main Content */}
            <div className="flex-1 p-6">
                <h2 className="text-2xl font-bold mb-6">Support Issues</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {issues.length === 0 && (
                        <div className="col-span-full text-center text-gray-400">No support issues found.</div>
                    )}
                    {issues.map(issue => (
                        <div key={issue.id} className={`rounded-lg shadow-lg p-6 bg-gray-800 border-2 ${issue.status === 'completed' ? 'border-green-500' : 'border-cyan-700'}`}>
                            <div className="flex justify-between items-center mb-2">
                                <span className="font-semibold text-cyan-400">{issue.email}</span>
                                <span className={`px-3 py-1 rounded text-xs font-bold ${issue.status === 'completed' ? 'bg-green-600 text-white' : 'bg-yellow-500 text-gray-900'}`}>{issue.status}</span>
                            </div>
                            <div className="mb-2 text-sm text-gray-300"><span className="font-semibold">Phone:</span> {issue.phone}</div>
                            <div className="mb-2 text-sm text-gray-300"><span className="font-semibold">Issue:</span> {issue.issue}</div>
                            <div className="mb-4 text-xs text-gray-400"><span className="font-semibold">Created:</span> {issue.createdAt && issue.createdAt.seconds ? new Date(issue.createdAt.seconds * 1000).toLocaleString() : ''}</div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handleEditClick(issue)}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-semibold"
                                >
                                    Edit
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                {/* Edit Modal */}
                {editIssue && (
                    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
                        <div className="bg-gray-900 rounded-lg shadow-lg p-8 w-full max-w-md relative">
                            <button onClick={handleModalClose} className="absolute top-2 right-2 text-gray-400 hover:text-white text-2xl">&times;</button>
                            <h3 className="text-xl font-bold mb-4">Edit Support Issue</h3>
                            <div className="mb-4">
                                <label className="block mb-1 font-semibold">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={editIssue.email}
                                    onChange={handleModalChange}
                                    className="w-full p-2 rounded bg-gray-800 text-white border border-gray-700"
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block mb-1 font-semibold">Phone</label>
                                <input
                                    type="text"
                                    name="phone"
                                    value={editIssue.phone}
                                    onChange={handleModalChange}
                                    className="w-full p-2 rounded bg-gray-800 text-white border border-gray-700"
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block mb-1 font-semibold">Issue</label>
                                <textarea
                                    name="issue"
                                    value={editIssue.issue}
                                    onChange={handleModalChange}
                                    className="w-full p-2 rounded bg-gray-800 text-white border border-gray-700"
                                    rows={3}
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block mb-1 font-semibold">Status</label>
                                <select
                                    name="status"
                                    value={editIssue.status}
                                    onChange={handleModalChange}
                                    className="w-full p-2 rounded bg-gray-800 text-white border border-gray-700"
                                >
                                    <option value="active">Active</option>
                                    <option value="completed">Completed</option>
                                </select>
                            </div>
                            {modalError && <div className="text-red-500 mb-2">{modalError}</div>}
                            <div className="flex gap-2 justify-end">
                                <button
                                    onClick={handleModalSave}
                                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-semibold"
                                    disabled={statusUpdating}
                                >
                                    {statusUpdating ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                    onClick={handleModalClose}
                                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded font-semibold"
                                    disabled={statusUpdating}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {/* Footer */}
            <footer className="py-8 bg-gray-800 mt-auto">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4 sm:gap-0">
                        <div className="text-gray-400 mb-2 sm:mb-0 text-center sm:text-left">
                            © 2025 Kifayti Retail Private Limited. All rights reserved.
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 items-center">
                            <Link to="/terms" className="text-gray-400 hover:text-cyan-400">
                                Terms & Conditions
                            </Link>
                            <Link to="/privacy" className="text-gray-400 hover:text-cyan-400">
                                Privacy Policy
                            </Link>
                            <Link to="/support" className="text-gray-400 hover:text-cyan-400">
                                Support
                            </Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default SupportAdmin; 